import { Box, Button, Link, Tag, Text } from '@lego/klik-ui';
import type { FC, MouseEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { createColumnHelper, type Row } from '@/components/common/table/appTable';
import { TableStoreProvider, useTableStore } from '@/components/common/table/store/Context';
import { Table } from '@/components/common/table/Table';
import { DismissalDialog } from '@/components/repository/DismissalDialog';
import { getSeverityWeight, SeverityDetails } from '@/components/vulnerabilities/SeverityDetails';
import type { ImageScanFinding, ImageScanResult, ImageTagApplication } from '@/hooks/repository';
import { useCanWriteById } from '@/hooks/workspace';

type Props = {
  scanResults?: ImageScanResult;
  title?: string;
  description?: ReactElement;
  applications?: ImageTagApplication[];
  workspaceId: number;
  repositoryId: number;
  imageTag: string;
};

const getDismissedApplicationIds = (finding: ImageScanFinding): Set<number> =>
  new Set((finding.dismissals ?? []).map((dismissal) => dismissal.applicationId));

const isFindingDismissedForAllApplications = (
  finding: ImageScanFinding,
  applications: ImageTagApplication[]
) => {
  const dismissedApplicationIds = getDismissedApplicationIds(finding);
  return (
    applications.length > 0 &&
    applications.every((application) => dismissedApplicationIds.has(application.id))
  );
};

type DismissalStatus = 'active' | 'all' | 'dismissed';

const DISMISSED_FILTER_ID = 'dismissed';
const ALL_DISMISSED_FILTER_VALUE: DismissalStatus = 'all';

export const matchesDismissalStatus = (
  finding: ImageScanFinding,
  applications: ImageTagApplication[],
  filterValue: DismissalStatus
) => {
  if (filterValue === 'active') return !isFindingDismissedForAllApplications(finding, applications);
  if (filterValue === 'dismissed')
    return isFindingDismissedForAllApplications(finding, applications);
  return true;
};

const columnHelper = createColumnHelper<ImageScanFinding>();

const DismissedFilterButton: FC = () => {
  const { filters, setFilters } = useTableStore((s) => s);
  const isActiveOnly = filters.some((f) => f.id === DISMISSED_FILTER_ID && f.value === 'active');

  const toggle = () => {
    setFilters((current) =>
      isActiveOnly
        ? [
            ...current.filter((filter) => filter.id !== DISMISSED_FILTER_ID),
            { id: DISMISSED_FILTER_ID, value: ALL_DISMISSED_FILTER_VALUE },
          ]
        : [
            ...current.filter((filter) => filter.id !== DISMISSED_FILTER_ID),
            { id: DISMISSED_FILTER_ID, value: 'active' },
          ]
    );
  };

  return (
    <Button onClick={toggle} size="sm" variant="outline">
      {isActiveOnly ? 'Show dismissed' : 'Hide dismissed'}
    </Button>
  );
};

export const ScanFindingsTable = ({
  scanResults,
  title: titleOverride,
  description,
  applications = [],
  workspaceId,
  repositoryId,
  imageTag,
}: Props) => {
  const [selectedFinding, setSelectedFinding] = useState<ImageScanFinding | undefined>();
  const canWrite = useCanWriteById(workspaceId);
  const findings = useMemo(() => scanResults?.findings ?? [], [scanResults]);

  const openDismissDialog = (finding: ImageScanFinding, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedFinding(finding);
  };

  const dismissalFilterFn = (
    row: Row<ImageScanFinding>,
    _columnId: string,
    filterValue: DismissalStatus
  ) => matchesDismissalStatus(row.original, applications, filterValue);

  const tableColumns = columnHelper.columns([
    columnHelper.accessor((row) => getSeverityWeight(row.severity), {
      header: 'Severity',
      id: 'severity',
      sortDescFirst: true,
      sortFn: 'basic',
      cell: (info) => {
        const severity = info.row.original.severity ?? 'UNDEFINED';
        return <SeverityDetails severity={severity} />;
      },
    }),
    columnHelper.accessor((row) => row.vulnerabilityId ?? '', {
      header: 'Vulnerability ID',
      id: 'vulnerabilityId',
      cell: (info) => {
        const finding = info.row.original;
        const value = info.getValue() as string;
        return finding.uri ? (
          <Link color="light-blue.600" href={finding.uri} isExternal>
            {value}
          </Link>
        ) : (
          <Text>{value}</Text>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        const name = row.packageName ?? '';
        const version = row.packageVersion;
        return version ? `${name}:${version}` : name;
      },
      {
        header: 'Package',
        id: 'packageName',
      }
    ),
    columnHelper.accessor((row) => row.description ?? '', {
      header: 'Description',
      id: 'description',
      minSize: 500,
      cell: (info) => (
        <Text noOfLines={1} title={info.getValue() as string} whiteSpace="normal">
          {info.getValue() as string}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => getDismissedApplicationIds(row).size, {
      header: 'Status',
      id: 'dismissed',
      filterFn: dismissalFilterFn,
      cell: (info) => {
        const dismissedCount = getDismissedApplicationIds(info.row.original).size;
        if (dismissedCount === 0) return <Text>Active</Text>;

        const fullyDismissed = isFindingDismissedForAllApplications(
          info.row.original,
          applications
        );
        return (
          <Tag colorScheme={fullyDismissed ? 'slate' : 'yellow'}>
            {fullyDismissed
              ? 'Dismissed'
              : `Dismissed for ${dismissedCount}/${applications.length}`}
          </Tag>
        );
      },
    }),
    ...(canWrite
      ? [
          columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: (info) => {
              const fullyDismissed = isFindingDismissedForAllApplications(
                info.row.original,
                applications
              );
              if (fullyDismissed) return null;

              return (
                <Button
                  isDisabled={
                    applications.length === 0 ||
                    !info.row.original.vulnerabilityId ||
                    !info.row.original.packageName
                  }
                  onClick={(event: MouseEvent<HTMLButtonElement>) =>
                    openDismissDialog(info.row.original, event)
                  }
                  size="sm"
                  variant="outline"
                >
                  Dismiss
                </Button>
              );
            },
          }),
        ]
      : []),
  ]);

  if (!scanResults) {
    return (
      <TableStoreProvider
        initialFilters={[{ id: DISMISSED_FILTER_ID, value: 'active' }]}
        initialSorting={[]}
        name="ScanFindings"
      >
        <Table
          columns={tableColumns}
          data={[]}
          description={description}
          title={titleOverride ?? 'Scan Findings (No scan results available)'}
        />
      </TableStoreProvider>
    );
  }

  const title = titleOverride ?? `Scan Findings (${findings.length})`;
  const storeName = `ScanFindings-${repositoryId}-${encodeURIComponent(imageTag)}`;

  return (
    <>
      <TableStoreProvider
        initialFilters={[{ id: DISMISSED_FILTER_ID, value: 'active' }]}
        initialSorting={[{ desc: true, id: 'severity' }]}
        name={storeName}
      >
        <Table
          columns={tableColumns}
          data={findings}
          description={description}
          expandableRow={(row) => (
            <Box backgroundColor="slate.10" px={4} py={2}>
              <Text whiteSpace="normal">{row.original.description}</Text>
            </Box>
          )}
          onRowClick={(_, row) => row.toggleExpanded()}
          title={title}
          toolbarAddon={<DismissedFilterButton />}
        />
      </TableStoreProvider>
      {selectedFinding && (
        <DismissalDialog
          applications={applications}
          finding={selectedFinding}
          imageTag={imageTag}
          onClose={() => setSelectedFinding(undefined)}
          repositoryId={repositoryId}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};
